import { useEffect, useState } from "react";
import papi, { formatErr } from "@/lib/partnerApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Loader2, LayoutDashboard, Radar, Users, Tags, ShieldCheck,
  BarChart3, Download, ScrollText, Settings, LogOut, Lock, Building2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import VdaDashboard from "./VdaDashboard";
import VdaDiscover from "./VdaDiscover";
import VdaProspects from "./VdaProspects";
import VdaCategories from "./VdaCategories";
import VdaVerification from "./VdaVerification";
import VdaReports from "./VdaReports";
import VdaExport from "./VdaExport";
import VdaActivity from "./VdaActivity";
import VdaSettings from "./VdaSettings";

const NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, C: VdaDashboard },
  { id: "discover", label: "Discover Vendors", icon: Radar, C: VdaDiscover },
  { id: "prospects", label: "Vendor Prospects", icon: Users, C: VdaProspects },
  { id: "categories", label: "Categories", icon: Tags, C: VdaCategories },
  { id: "verification", label: "Verification", icon: ShieldCheck, C: VdaVerification },
  { id: "reports", label: "Reports", icon: BarChart3, C: VdaReports },
  { id: "export", label: "Export", icon: Download, C: VdaExport },
  { id: "activity", label: "Activity Logs", icon: ScrollText, C: VdaActivity },
  { id: "settings", label: "Settings", icon: Settings, C: VdaSettings },
];

function Gate({ onAuthed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await papi.post("/partner/auth/login", { email, password });
      if (data.user.role !== "admin") { toast.error("VDA is restricted to Super Admins."); setLoading(false); return; }
      localStorage.setItem("partner_token", data.token);
      localStorage.setItem("partner_role", data.user.role);
      onAuthed(data.user);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center px-6">
      <div className="mb-8 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-500"><Building2 className="h-6 w-6 text-white" /></div>
        <div>
          <p className="font-display text-xl font-extrabold text-white">Vendor Discovery Agent</p>
          <p className="text-xs text-slate-400">Nirmantra · Internal Ops Tool</p>
        </div>
      </div>
      <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-8">
        <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-teal-500/15"><Lock className="h-6 w-6 text-teal-400" /></div>
        <h1 className="font-display text-2xl font-extrabold text-white">Restricted Access</h1>
        <p className="mt-1 text-sm text-slate-400">Super Admin credentials required.</p>
        <div className="mt-6 space-y-4">
          <div><Label className="text-slate-300">Email</Label><Input data-testid="vda-login-email" className="mt-1.5 rounded-xl border-slate-700 bg-slate-800 text-white" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} /></div>
          <div><Label className="text-slate-300">Password</Label><Input data-testid="vda-login-password" type="password" className="mt-1.5 rounded-xl border-slate-700 bg-slate-800 text-white" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} /></div>
          <Button data-testid="vda-login-submit" onClick={submit} disabled={loading} className="h-11 w-full rounded-xl bg-teal-500 text-white hover:bg-teal-600">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}</Button>
        </div>
      </div>
    </div>
  );
}

export default function VDA() {
  const [state, setState] = useState("loading"); // loading | gate | ready
  const [user, setUser] = useState(null);
  const [active, setActive] = useState("dashboard");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await papi.get("/partner/auth/me");
        if (data.role === "admin") { setUser(data); setState("ready"); }
        else setState("gate");
      } catch { setState("gate"); }
    })();
  }, []);

  const logout = () => {
    localStorage.removeItem("partner_token");
    localStorage.removeItem("partner_role");
    setUser(null); setState("gate");
  };

  if (state === "loading") return <div className="flex min-h-screen items-center justify-center bg-slate-950"><Loader2 className="h-7 w-7 animate-spin text-teal-400" /></div>;
  if (state === "gate") return <Gate onAuthed={(u) => { setUser(u); setState("ready"); }} />;

  const Active = NAV.find((n) => n.id === active)?.C || VdaDashboard;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 flex w-64 flex-col bg-slate-950 text-slate-300">
        <div className="flex items-center gap-3 border-b border-slate-800 px-5 py-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-500"><Building2 className="h-5 w-5 text-white" /></div>
          <div>
            <p className="font-display text-sm font-extrabold leading-tight text-white">Vendor Discovery</p>
            <p className="text-[11px] text-slate-500">Internal Ops · Nirmantra</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4" data-testid="vda-nav">
          {NAV.map((n) => (
            <button key={n.id} data-testid={`vda-nav-${n.id}`} onClick={() => setActive(n.id)}
              className={cn("flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors",
                active === n.id ? "bg-teal-500 text-white" : "text-slate-400 hover:bg-slate-800/70 hover:text-white")}>
              <n.icon className="h-[18px] w-[18px]" strokeWidth={1.75} /> {n.label}
            </button>
          ))}
        </nav>
        <div className="border-t border-slate-800 p-3">
          <div className="mb-2 px-2 text-xs text-slate-500">{user?.email}</div>
          <button data-testid="vda-logout" onClick={logout} className="flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium text-slate-400 transition-colors hover:bg-slate-800/70 hover:text-white">
            <LogOut className="h-[18px] w-[18px]" strokeWidth={1.75} /> Sign out
          </button>
        </div>
      </aside>

      <main className="ml-64 min-h-screen px-8 py-8">
        <Active goTo={setActive} />
      </main>
    </div>
  );
}
